import {
    hasAnyGoalTarget,
    isOutcomeGoalMetric,
    MarketingGoalTargets,
    UserId,
    zeroTargets,
} from "@sp-crm/core";
import {
    flattenTargets,
    TargetRow,
    TargetRowSection,
    unknownActivityRow,
} from "./goal-target-rows";
import {
    GoalVersionLike,
    resolveActiveGoalVersion,
    targetsKey,
    versionTargets,
} from "./goal-versions";
import { ymAddMonths } from "./pace";

export type GoalRowSource = "scheduled" | "carriedForward" | "none";

export interface MarketerGoalRow {
    marketerUserId: UserId;
    targets: MarketingGoalTargets;
    source: GoalRowSource;
    startedMonth: string | null;
}

export interface GoalGridSaveGroup extends MarketingGoalTargets {
    marketerUserIds: UserId[];
}

export interface GoalDraft {
    marketerUserIds: UserId[];
    columnKeys: string[];
    values: Record<string, Record<string, number>>;
}

export const marketerGoalRowsForMonth = (
    versions: GoalVersionLike[],
    marketerUserIds: UserId[],
    month: string,
): MarketerGoalRow[] =>
    marketerUserIds.map((marketerUserId): MarketerGoalRow => {
        const active = resolveActiveGoalVersion(
            versions.filter(version => version.marketerUserId === marketerUserId),
            month,
        );
        if (!active) {
            return {
                marketerUserId,
                targets: zeroTargets(),
                source: "none",
                startedMonth: null,
            };
        }
        return {
            marketerUserId,
            targets: versionTargets(active),
            source: active.effectiveMonth === month ? "scheduled" : "carriedForward",
            startedMonth: active.effectiveMonth,
        };
    });

export const goalRowsWithTargets = (rows: MarketerGoalRow[]): MarketerGoalRow[] =>
    rows.filter(row => hasAnyGoalTarget(row.targets));

export const goalColumnKeysForRows = (rows: MarketerGoalRow[]): string[] => {
    const keys: string[] = [];
    for (const row of rows) {
        for (const [key, target] of Object.entries(flattenTargets(row.targets))) {
            if (target > 0 && !keys.includes(key)) {
                keys.push(key);
            }
        }
    }
    return keys;
};

export const targetsFromValues = (
    values: Record<string, number>,
): MarketingGoalTargets => {
    const targets = zeroTargets();
    for (const [key, target] of Object.entries(values)) {
        if (target <= 0) {
            continue;
        }
        if (isOutcomeGoalMetric(key)) {
            targets[key] = target;
        } else {
            targets.activity[key] = target;
        }
    }
    return targets;
};

export const goalDraftFromRows = (rows: MarketerGoalRow[]): GoalDraft => ({
    marketerUserIds: goalRowsWithTargets(rows).map(row => row.marketerUserId),
    columnKeys: goalColumnKeysForRows(rows),
    values: Object.fromEntries(
        rows.map(row => [row.marketerUserId, flattenTargets(row.targets)]),
    ),
});

export const goalDraftTargets = (
    draft: GoalDraft,
    marketerUserId: UserId,
): MarketingGoalTargets => {
    if (!draft.marketerUserIds.includes(marketerUserId)) {
        return zeroTargets();
    }
    const values = draft.values[marketerUserId] ?? {};
    return targetsFromValues(
        Object.fromEntries(draft.columnKeys.map(key => [key, values[key] ?? 0])),
    );
};

export const goalDraftValue = (
    draft: GoalDraft,
    marketerUserId: UserId,
    key: string,
): number => draft.values[marketerUserId]?.[key] ?? 0;

export const goalDraftMarketerIds = (draft: GoalDraft, order: UserId[]): UserId[] =>
    order.filter(marketerUserId => draft.marketerUserIds.includes(marketerUserId));

export const goalDraftChangedMarketerIds = (
    rows: MarketerGoalRow[],
    draft: GoalDraft,
): UserId[] =>
    rows
        .filter(
            row =>
                targetsKey(goalDraftTargets(draft, row.marketerUserId)) !==
                targetsKey(row.targets),
        )
        .map(row => row.marketerUserId);

export const goalDraftSaveGroups = (
    rows: MarketerGoalRow[],
    draft: GoalDraft,
): GoalGridSaveGroup[] =>
    goalDraftChangedMarketerIds(rows, draft).map(marketerUserId => ({
        marketerUserIds: [marketerUserId],
        ...goalDraftTargets(draft, marketerUserId),
    }));

export const setGoalDraftValue = (
    draft: GoalDraft,
    marketerUserId: UserId,
    key: string,
    target: number,
): GoalDraft => ({
    ...draft,
    values: {
        ...draft.values,
        [marketerUserId]: { ...(draft.values[marketerUserId] ?? {}), [key]: target },
    },
});

export const uniformGoalDraftValue = (
    draft: GoalDraft,
    marketerUserIds: UserId[],
    key: string,
): number | null => {
    if (marketerUserIds.length === 0) {
        return null;
    }
    const values = marketerUserIds.map(marketerUserId =>
        goalDraftValue(draft, marketerUserId, key),
    );
    return values.every(value => value === values[0]) ? values[0] : null;
};

export const fillGoalDraftColumn = (
    draft: GoalDraft,
    marketerUserIds: UserId[],
    key: string,
    target: number,
): GoalDraft =>
    marketerUserIds.reduce(
        (current, marketerUserId) =>
            setGoalDraftValue(current, marketerUserId, key, target),
        draft,
    );

export const addGoalDraftColumn = (draft: GoalDraft, key: string): GoalDraft =>
    draft.columnKeys.includes(key)
        ? draft
        : { ...draft, columnKeys: [...draft.columnKeys, key] };

export const removeGoalDraftColumn = (draft: GoalDraft, key: string): GoalDraft => ({
    ...draft,
    columnKeys: draft.columnKeys.filter(candidate => candidate !== key),
});

export const addGoalDraftMarketer = (
    draft: GoalDraft,
    marketerUserId: UserId,
): GoalDraft => {
    if (draft.marketerUserIds.includes(marketerUserId)) {
        return draft;
    }
    const values = { ...(draft.values[marketerUserId] ?? {}) };
    for (const key of draft.columnKeys) {
        const shared = uniformGoalDraftValue(draft, draft.marketerUserIds, key);
        if (shared !== null && (values[key] ?? 0) === 0) {
            values[key] = shared;
        }
    }
    return {
        ...draft,
        marketerUserIds: [...draft.marketerUserIds, marketerUserId],
        values: { ...draft.values, [marketerUserId]: values },
    };
};

export const removeGoalDraftMarketer = (
    draft: GoalDraft,
    marketerUserId: UserId,
): GoalDraft => ({
    ...draft,
    marketerUserIds: draft.marketerUserIds.filter(
        candidate => candidate !== marketerUserId,
    ),
});

export const setGoalDraftMarketers = (
    draft: GoalDraft,
    marketerUserIds: UserId[],
): GoalDraft => {
    const withoutUnchosen = draft.marketerUserIds
        .filter(marketerUserId => !marketerUserIds.includes(marketerUserId))
        .reduce(
            (current, marketerUserId) => removeGoalDraftMarketer(current, marketerUserId),
            draft,
        );
    return marketerUserIds.reduce(
        (current, marketerUserId) => addGoalDraftMarketer(current, marketerUserId),
        withoutUnchosen,
    );
};

export const parseGoalTarget = (raw: string): number =>
    Math.max(0, parseInt(raw || "0", 10) || 0);

export const goalGridColumns = (
    keys: string[],
    sections: TargetRowSection[],
): TargetRow[] => {
    const allRows = sections.flatMap(section => section.rows);
    const shown = allRows.filter(row => keys.includes(row.key));
    const unknown = keys
        .filter(key => !allRows.some(row => row.key === key))
        .map(unknownActivityRow);
    return [
        ...shown.filter(row => row.kind === "outcome"),
        ...shown.filter(row => row.kind === "activity"),
        ...unknown,
    ];
};

export const goalApplyMonthOptions = (
    versions: GoalVersionLike[],
    firstMonth: string,
    monthsAhead: number,
): string[] => {
    const months = new Set(
        Array.from({ length: monthsAhead + 1 }, (_, index) =>
            ymAddMonths(firstMonth, index),
        ),
    );
    for (const version of versions) {
        if (version.effectiveMonth > firstMonth) {
            months.add(version.effectiveMonth);
        }
    }
    return Array.from(months).sort();
};

export const scheduledGoalMonths = (
    versions: GoalVersionLike[],
    afterMonth: string,
): string[] =>
    Array.from(
        new Set(
            versions
                .filter(version => version.effectiveMonth > afterMonth)
                .map(version => version.effectiveMonth),
        ),
    ).sort();

export const scheduledGoalMarketerIds = (
    versions: GoalVersionLike[],
    month: string,
): UserId[] =>
    Array.from(
        new Set(
            versions
                .filter(version => version.effectiveMonth === month)
                .map(version => version.marketerUserId),
        ),
    );
